/**
 * CubeDelta — transient floating `+N` / `−N` text rendered in SVG space at a
 * territory's centroid.
 *
 * Visible feedback layer: when a territory's `cubeCount` ticks up or down
 * between two consecutive `gameState` snapshots, this component blooms a
 * brief floating indicator over the affected territory. The player gets a
 * visceral cue that something happened (auto-deploy reinforcements, combat
 * losses) instead of having to spot a numeric change in dense text.
 *
 * Self-managing lifecycle: the parent renders one of these per delta event,
 * and the component fires `onComplete` once its animation settles so the
 * parent can drop it from the active list.
 *
 *   - Initial: opacity 0, y +0, scale 0.6
 *   - Animate: opacity 0 → 1 → 1 → 0, y → -28, scale → 1 over ~1100ms
 *   - On finish: onComplete()
 *
 * Honours `prefers-reduced-motion`: skips the y-translate and the scale-in,
 * keeping a 1-second static fade so the information still reaches the user
 * without any vestibular trigger.
 *
 * Must be rendered as a child of an `<svg>` (it returns `<motion.g>`); use
 * the wrapping <CubeDeltaOverlay> if you don't already have an SVG context.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { motion, useAnimate } from 'motion/react';
import { surfaces, fontStacks } from '@qd/design-tokens';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export interface CubeDeltaProps {
  readonly territoryId: string;
  readonly delta: number;
  readonly centroid: { readonly x: number; readonly y: number };
  readonly onComplete: () => void;
}

const ANIMATION_MS = 1100;
const RISE_PX = 28;

export function CubeDelta({ delta, centroid, onComplete }: CubeDeltaProps): ReactNode {
  const reduced = useReducedMotion();
  const [scope, animate] = useAnimate<SVGGElement>();

  // Capture onComplete in a ref so it never becomes a useEffect dependency.
  // If onComplete were in the dep array, a parent re-render (e.g. setDeltas
  // being called to dismiss a different delta) would create a new function
  // reference and re-trigger this effect — restarting the animation from
  // scratch and creating the endless loop.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  // Drive the lifecycle imperatively so we can fire `onComplete` exactly once
  // the keyframe sequence resolves. Returning early on `reduced` still calls
  // onComplete after a 900ms hold so the indicator is read but not lingering.
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;

    const done = () => {
      if (!cancelled) onCompleteRef.current();
    };

    if (reduced) {
      // Static reveal: opacity-only fade in & out.
      void animate(scope.current, { opacity: [0, 1, 1, 0] }, { duration: 0.9, times: [0, 0.15, 0.7, 1] }).then(() => {
        done();
      });
    } else {
      // Full lift with scale + translate.
      void animate(
        scope.current,
        {
          opacity: [0, 1, 1, 0],
          y: [0, -RISE_PX * 0.4, -RISE_PX * 0.85, -RISE_PX],
          scale: [0.6, 1.05, 1, 0.95],
        },
        {
          duration: ANIMATION_MS / 1000,
          times: [0, 0.18, 0.6, 1],
          ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
        },
      ).then(() => {
        done();
      });
    }

    // Safety: never let an indicator hang. Fall back to a hard timeout
    // 200ms longer than the longest path so a dropped frame doesn't strand
    // the element on screen.
    timeoutId = window.setTimeout(() => {
      done();
    }, ANIMATION_MS + 200);

    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
    // Empty dep array: run once on mount, clean up on unmount.
    // onComplete is accessed via ref to avoid re-triggering the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animate, scope, reduced]);

  const isGain = delta > 0;
  const fill = isGain ? surfaces.waxRed : surfaces.ink;
  const label = isGain ? `+${delta}` : `${delta}`; // negative already has minus

  return (
    <motion.g
      ref={scope}
      initial={{ opacity: 0, y: 0, scale: reduced ? 1 : 0.6 }}
      style={{ transformOrigin: `${centroid.x}px ${centroid.y}px`, pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {/* Paper-coloured halo for legibility on any territory fill. The
          underlying text element is drawn twice: once as a thicker
          paper-coloured stroke, once as the coloured fill. Cheaper than a
          filter and reads cleanly across the board. */}
      <text
        x={centroid.x}
        y={centroid.y}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={fontStacks.mono}
        fontSize={18}
        fontWeight={700}
        stroke={surfaces.paper}
        strokeWidth={4}
        strokeLinejoin="round"
        paintOrder="stroke"
        fill={fill}
      >
        {label}
      </text>
      <text
        x={centroid.x}
        y={centroid.y}
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={fontStacks.mono}
        fontSize={18}
        fontWeight={700}
        fill={fill}
      >
        {label}
      </text>
    </motion.g>
  );
}
